import React from 'react';

import { useAutoIncrementId } from '../../hooks/use-auto-increment-id';
import { bemBlock } from '../../modules/bem';
import { Layout } from '../layout/Layout';
import { ToggleSwitch } from '../toggle-switch/ToggleSwitch';

import { getDataRole } from './Panel.helpers';
import { type DataRoleProps } from './Panel.types';

import './PanelTabs.less';

const block = bemBlock('n-Panel');

type InactiveTabPanelProps = React.HTMLAttributes<HTMLElement> & {
  inert?: '';
};

export type PanelTabItem<T extends string> = {
  content: React.ReactNode;
  label: React.ReactNode;
  value: T;
};

export type PanelTabsProps<T extends string> = DataRoleProps & {
  items: Array<PanelTabItem<T>>;
  onValueChange: (value: T) => void;
  reservePanelHeight?: boolean;
  value: T;
};

export function PanelTabs<T extends string>({
  dataRole,
  items,
  onValueChange,
  reservePanelHeight,
  value,
  'data-role': dataRoleAttribute,
}: PanelTabsProps<T>) {
  const tabsId = useAutoIncrementId('n-panel-tabs');

  if (items.length === 0) {
    return null;
  }

  const activeItem = items.find((item) => item.value === value) ?? items[0];

  return (
    <Layout.Element className={block('tabs')} data-role={getDataRole(dataRole, dataRoleAttribute)}>
      <ToggleSwitch
        className={block('tab-switch')}
        role="tablist"
        selected={activeItem.value}
        onValueChange={(nextValue) => onValueChange(nextValue as T)}
      >
        {items.map((item, index) => (
          <ToggleSwitch.Button
            key={item.value}
            id={`${tabsId}-tab-${index}`}
            className={block('tab-switch-button')}
            value={item.value}
            role="tab"
            type="button"
            aria-controls={`${tabsId}-panel-${index}`}
            aria-selected={item.value === activeItem.value}
          >
            {item.label}
          </ToggleSwitch.Button>
        ))}
      </ToggleSwitch>
      <Layout.Element
        className={block({ element: 'tab-panels', modifiers: { reserved: reservePanelHeight } })}
      >
        {items.map((item, index) => {
          const isActive = item.value === activeItem.value;

          return (
            <Layout.Element
              {...getTabPanelVisibilityProps({ isActive, reservePanelHeight })}
              key={item.value}
              id={`${tabsId}-panel-${index}`}
              aria-labelledby={`${tabsId}-tab-${index}`}
              className={block({ element: 'tab-panel', modifiers: { active: isActive } })}
              role="tabpanel"
            >
              {item.content}
            </Layout.Element>
          );
        })}
      </Layout.Element>
    </Layout.Element>
  );
}

function getTabPanelVisibilityProps({
  isActive,
  reservePanelHeight,
}: {
  isActive: boolean;
  reservePanelHeight?: boolean;
}): InactiveTabPanelProps | undefined {
  if (isActive) {
    return undefined;
  }

  if (!reservePanelHeight) {
    return {
      hidden: true,
    };
  }

  return {
    'aria-hidden': true,
    inert: '',
  };
}
